import React, { useState } from "react";
import Modal from "../../components/Modal";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { XMarkIcon } from "@heroicons/react/20/solid";
import {
  ManifestHeaderInterface,
  ManifestDetailInterface,
} from "../../interfaces/ManifestInterface";
import ManifestHeaderData from "./ManifestHeaderData";
import ManifestDetailsTable from "./ManifestDetailTable";
import { PrimaryButton } from "../../components/Buttons";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import DropdownMenu from "../../components/DropdownMenu";
import downloadManifest from "../../services/manifestServices";
import { useAppSelector } from "../../store/hooks";
import classNames from "classnames";
import moment from "moment";

interface ManifestModalProps {
  showConsignee?: boolean;
  showPaymentMode?: boolean;
  showDeliveryAddress?: boolean;
  printRawExcel?: boolean;
  manifest: ManifestHeaderInterface;
  detailData: ManifestDetailInterface[];
  openModal: boolean;
  setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
}

enum ManifestReportOption {
  DETALLADO,
  AGRUPADO,
}
const reportOptions = [
  {
    title: "DETALLADO",
    value: ManifestReportOption.DETALLADO,
  },
  {
    title: "AGRUPADO",
    value: ManifestReportOption.AGRUPADO,
  },
];

const ManifestDetailModal: React.FC<ManifestModalProps> = ({
  showConsignee = false,
  showPaymentMode = false,
  showDeliveryAddress = false,
  printRawExcel = false,
  manifest,
  detailData,
  openModal,
  setOpenModal,
}) => {
  const [openPdfPopover, setOpenPdfPopover] = useState(false);
  const [openExcelPopover, setOpenExcelPopover] = useState(false);
  const user = useAppSelector((state) => state.user);

  const handleDownloadClick = async (type: number) => {
    downloadManifest(
      manifest.masterShipmentHeaderCode as string,
      type, // 1=AGRUPADO. 2=DETALLADO
      user.user?.userLogin as string,
      openPdfPopover === true ? 1 : 2 // 1=PDF. 2=EXCEL
    );
  };

  const handleDropdownMenuSelect = (
    option: { value: ManifestReportOption },
    type: "pdf" | "excel"
  ) => {
    if (type === "pdf") {
      setOpenExcelPopover(false);
      setOpenPdfPopover(true);
    } else {
      setOpenPdfPopover(false);
      setOpenExcelPopover(true);
    }
    if (option.value === ManifestReportOption.AGRUPADO) {
      handleDownloadClick(1); // 1= AGRUPADO.
    } else {
      handleDownloadClick(2); //2= DETALLADO
    }
  };

  const exportToExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Reporte de Envíos");

    worksheet.columns = [
      { key: "retiro", width: 15 },
      { key: "fechaEnvio", width: 25 },
      { key: "guia", width: 15 },
      { key: "origen", width: 10 },
      { key: "servicio", width: 10 },
      { key: "modalidadPago", width: 20 },
      { key: "peso", width: 15 },
      { key: "piezas", width: 10 },
      { key: "remitente", width: 25 },
      { key: "destinatario", width: 25 },
      { key: "rif", width: 15 },
      { key: "telefono", width: 15 },
      { key: "destino", width: 25 },
      { key: "entrega", width: 25 },
    ];

    // Manifest
    const row2 = worksheet.getRow(2);
    row2.getCell(
      1
    ).value = `Manifiesto de Despacho en Tránsito ${manifest.masterShipmentHeaderCode}`;
    row2.font = { size: 12 };

    // Business unit
    const row3 = worksheet.getRow(3);
    row3.getCell(
      1
    ).value = `Tienda ${user.businessUnit?.code} - ${user.businessUnit?.name}`;
    row3.font = { size: 12 };

    // User
    const row4 = worksheet.getRow(4);
    row4.getCell(1).value = `Elaborado por ${user.user?.userName} ${
      user.user?.userLastName
    } (${new Date().toLocaleString()})`;

    // Header
    const headerRow = worksheet.getRow(7);
    headerRow.values = [
      "Retiro",
      "Fecha Envío",
      "Guía",
      "Origen",
      "Servicio",
      "Modalidad de Pago",
      "Peso Físico (kg)",
      "Pieza(s)",
      "Remitente",
      "Destinatario",
      "RIF",
      "Teléfono",
      "Destino",
      "Dirección de Entrega",
    ];
    headerRow.font = { bold: true };
    headerRow.eachCell((cell) => {
      cell.border = {
        bottom: { style: "thin" },
      };
    });

    // Data
    detailData.forEach((item) => {
      const row = worksheet.addRow({
        retiro: item.isRetiraOficina ? "Oficina" : "Domicilio",
        fechaEnvio: moment(item.creationDateShipment).format(
          "DD/MM/YYYY hh:mm:ss a"
        ),
        guia: item.shipmentNumber,
        origen: item.buCodeSource,
        servicio: item.serviceName,
        modalidadPago: item.paymentModeName,
        peso: item.physicalWeight.toFixed(2),
        piezas: item.pieceNumber,
        remitente: item.shipperName,
        destinatario: item.consigneeName,
        rif: item.consigneeIdentification,
        telefono: item.consigneePhone,
        destino: item.locationDestinationCode,
        entrega: item.isRetiraOficina ? "En Tienda" : item.locationName,
      });

      row.getCell(6).numFmt = "0.00";
    });

    // Download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, "ReporteEnvios.xlsx");
  };

  return (
    <Modal
      openModal={openModal}
      setOpenModal={setOpenModal}
      className="bg-gray-200"
    >
      <div className="p-4 flex flex-1 flex-col min-w-[60rem] mb-4">
        {/* Header Title */}
        <div className="flex justify-between">
          <div>
            <span className="font-bold text-xl">Manifiesto: </span>
            <span className="text-indigo-600 font-bold text-xl">
              {manifest.masterShipmentHeaderCode}
            </span>
          </div>

          <div className="flex items-center justify-end">
            <button
              type="button"
              className="flex items-center justify-center relative rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              onClick={() => setOpenModal(false)}
            >
              <span className="absolute -inset-2.5" />
              <span className="sr-only">Cerrar panel</span>
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
        </div>

        <div className="w-full mb-1">
          <span className="text-gray-600">Fecha: </span>
          <span className="text-gray-800">
            {!!manifest?.manifestClosedDate &&
              new Date(manifest.manifestClosedDate)
                .toLocaleDateString("es-VE", {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                })
                .replace("a", "AM")
                .replace("p", "PM")
                .slice(0, -4)}
          </span>
        </div>
      </div>

      <hr className="my-1 mx-2 mb-2" />

      {/* Header data */}
      <div className="flex flex-1 gap-4">
        {/* Shipper */}
        <ManifestHeaderData
          masterShipmentHeaderID={manifest.masterShipmentHeaderID}
          masterShipmentHeaderCode={manifest.masterShipmentHeaderCode}
          manifestTypeID={manifest.manifestTypeID}
          buCodeSource={manifest.buCodeSource}
          buCodeDestination={manifest.buCodeDestination}
          route={manifest.route?.toUpperCase()}
          creationDate={manifest.creationDate}
          transport={
            manifest.transportModelName?.toUpperCase() +
            " - " +
            manifest.transportPlate?.toUpperCase()
          }
          driver1Name={manifest.driver1Name?.toUpperCase()}
          statusName={manifest?.statusName?.toUpperCase() || ""}
        />
      </div>

      {/* Línea separadora */}
      <hr className="my-2 mx-2" />

      {/* Detalles del manifiesto */}
      <div className="gap-4 p-2">
        <ManifestDetailsTable
          showConsignee={showConsignee}
          showPaymentMode={showPaymentMode}
          showDeliveryAddress={showDeliveryAddress}
          data={detailData}
          showItem={false}
          showAllStatus={true}
        />
      </div>

      <div
        className={classNames(
          "flex flex-1 flex-row mt-8",
          printRawExcel && "hidden"
        )}
      >
        <div className="relative my-4 ml-2">
          <PrimaryButton
            onClick={() => setOpenPdfPopover(!openPdfPopover)}
            className="relative w-28"
          >
            PDF
            {openPdfPopover ? (
              <ChevronUpIcon
                className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 ml-2"
                aria-hidden="true"
              />
            ) : (
              <ChevronDownIcon
                className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 ml-2"
                aria-hidden="true"
              />
            )}
          </PrimaryButton>
          <DropdownMenu
            open={openPdfPopover}
            setOpen={setOpenPdfPopover}
            onSelect={(option) => handleDropdownMenuSelect(option, "pdf")}
            options={reportOptions}
            className="!mt-2 w-full transition ease-in-out duration-300"
          />
        </div>

        <div className="relative my-4 ml-2">
          <PrimaryButton
            onClick={() => setOpenExcelPopover(!openExcelPopover)}
            className="relative w-28"
          >
            EXCEL
            {openExcelPopover ? (
              <ChevronUpIcon
                className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 ml-2"
                aria-hidden="true"
              />
            ) : (
              <ChevronDownIcon
                className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 ml-2"
                aria-hidden="true"
              />
            )}
          </PrimaryButton>
          <DropdownMenu
            open={openExcelPopover}
            setOpen={setOpenExcelPopover}
            onSelect={(option) => handleDropdownMenuSelect(option, "excel")}
            options={reportOptions}
            className="!mt-2 w-full transition ease-in-out duration-300"
          />
        </div>
      </div>

      <div
        className={classNames(
          "flex flex-1 flex-row mt-8",
          !printRawExcel && "hidden"
        )}
      >
        <PrimaryButton className="w-44" onClick={exportToExcel}>
          Descargar Reporte
        </PrimaryButton>
      </div>
    </Modal>
  );
};
export default ManifestDetailModal;
